'use client';

import {
  CSSProperties,
  ReactNode,
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { ToastOptions, toast } from 'react-hot-toast';

import { useNotifications, useSchemes } from '@personly/libs-providers';
import type { Icon } from '@personly/libs-types';
import { Toast } from '@personly/libs-widgets';
import { deploymentUrl } from '@personly/libs-urls';
import { toPathname } from '@personly/libs-pathnames';

const Toaster = lazy(() =>
  import('react-hot-toast').then((module) => ({ default: module.Toaster }))
);

const ToastBar = lazy(() =>
  import('react-hot-toast').then((module) => ({ default: module.ToastBar }))
);

const onNotify = async (
  locale: string,
  handle: string,
  substitutions: Record<string, string> = {}
) => {
  const pathnames = ['translate', locale, handle];

  const url = deploymentUrl(toPathname(pathnames.join('/')));

  for (const key of Object.keys(substitutions)) {
    url.searchParams.set(key, substitutions[key]);
  }

  const response = await fetch(url);
  const { result } = await response.json();

  return result ? result.value : handle;
};

interface Props {
  children: ReactNode;
  locale: string;
  toastIcons: Icon[];
}

const Notifications = ({ children, locale, toastIcons }: Props) => {
  const { cssVariables } = useSchemes();
  const { removeNotification, notifications } = useNotifications();

  const style = useMemo<CSSProperties>(
    () => ({
      backgroundColor: cssVariables.toastBackgroundColor[0],
      border: `1px solid ${cssVariables.toastBorderColor}`,
      color: cssVariables.toastTextColor[0],
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
      alignItems: 'normal',
      padding: 0,
    }),
    [
      cssVariables.toastBackgroundColor,
      cssVariables.toastBorderColor,
      cssVariables.toastTextColor,
    ]
  );

  useEffect(() => {
    (async () => {
      for (const notification of notifications) {
        const options: ToastOptions = {
          position: 'top-center',
          id: notification.handle,
          duration: notification.duration,
          style,
        };

        const message = await onNotify(
          locale,
          notification.handle,
          notification.substitutions
        );

        if (notification.type === 'NOTICE') {
          toast(message, options);
        } else if (notification.type === 'SUCCESS') {
          toast.success(message, options);
        } else if (notification.type === 'ERROR') {
          toast.error(message, options);
        }

        if (notification.duration !== Infinity) {
          setTimeout(() => {
            removeNotification(notification.handle, () => {});
          }, notification.duration);
        }
      }
    })();
  }, [notifications, removeNotification, style, locale]);

  const dismiss = useCallback(
    (id: string) => removeNotification(id, () => toast.dismiss(id)),
    [removeNotification]
  );

  const toastIcon = (handle: string) =>
    toastIcons.find((icon) => icon.handle === handle);

  return (
    <Suspense>
      <Toaster>
        {(t) => (
          <ToastBar toast={t}>
            {({ message }) => (
              <Toast
                dismiss={() => dismiss(t.id)}
                icon={toastIcon(t.type)}
                message={message}
                type={t.type}
              >
                {children}
              </Toast>
            )}
          </ToastBar>
        )}
      </Toaster>
    </Suspense>
  );
};

export default Notifications;
